import React from "react"
import { Modal, Form, Col, Row, Button } from "react-bootstrap"
import ReCAPTCHA from "react-google-recaptcha"
import { toast } from "react-toastify"
import { GetInvitationAttempts } from "../../api/Invitation/GetInvitationStatus"
import GetOneTimePIN from "../../api/Invitation/GetOneTimePIN"
import CheckOneTimePIN from "../../api/Invitation/Model/CheckOneTimePIN"
import ValidatePINModel from "../../api/Invitation/Model/ValidatePINModel"
import ValidateOneTimePIN from "../../api/Invitation/ValidateOneTimePIN"
import { APIError } from "../../api/ObservableFromFetch"
import { ReCaptchaSiteKey } from "../../config/Constants"
import ActionButton from "../ActionButton"
import CountdownButton from "../CountdownButton"

interface ValidatePINProps {
  currentURL: string
  outboxAN: number
  onSuccess: (pin: string) => void
  onBack?: () => void
}

interface ValidatePINState {
  pinValid?: boolean
  showValidatePIN: boolean
  didNotReceivePIN: boolean
  pinDidNotMatch: boolean
  requestNewPIN: boolean
  validCaptcha?: boolean
  showTooManyAttempts: boolean
  loading: boolean
  outboxAN: number
}

export default class ValidatePIN extends React.PureComponent<ValidatePINProps, ValidatePINState> {
  currentPin: string = ""
  state = { showValidatePIN: true, didNotReceivePIN: false, pinDidNotMatch: false, requestNewPIN: false, validCaptcha: false, showTooManyAttempts: false, loading: false, outboxAN: this.props.outboxAN }

  validatePIN(e: React.KeyboardEvent<HTMLInputElement>) {
    const enteredPIN = (e.target as HTMLInputElement).value
    if (enteredPIN.length === 6) {
      this.currentPin = enteredPIN
      this.setState({ loading: true })
      const data: ValidatePINModel = {
        Link: this.props.currentURL,
        OneTimePin: this.currentPin,
        OutboxAN: this.state.outboxAN
      }
      ValidateOneTimePIN(data).subscribe(
        data => {
          this.setState({ loading: false })
          this.validateOneTimePinSuccess(data)
        },
        error => {
          GetInvitationAttempts(this.props.currentURL).subscribe(
            data => {
              if (data > 4) this.setState({ loading: false, showValidatePIN: false, showTooManyAttempts: true })
              else this.setState({ loading: false, showValidatePIN: false, pinDidNotMatch: true })
            },
            error => {
              this.handleAPIError(error)
              this.setState({ loading: false })
            }
          )
          this.handleAPIError(error)
        }
      )
    }
  }

  // TODO: replace type
  validateOneTimePinSuccess(data: any) {
    // TODO: verify IsConfirmation logic
    // TODO: verify configure-password logic
    this.setState({ showValidatePIN: false })
    toast.success(data)
    this.props.onSuccess(this.currentPin)
  }

  goToLogin() {
    window.location.hash = "#/"
  }

  requestNewPIN(): void {
    const data: CheckOneTimePIN = { Link: this.props.currentURL, method: "email" }
    this.setState({ loading: true })
    GetOneTimePIN(data).subscribe(
      data => this.sendOneTimePINSuccess(data),
      error => {
        this.setState({ loading: false })
        this.handleAPIError(error)
      }
    )
  }

  sendOneTimePINSuccess(data: any) {
    this.setState({ showValidatePIN: true, didNotReceivePIN: false, pinDidNotMatch: false, requestNewPIN: false, validCaptcha: false, loading: false, outboxAN: data })
    toast.success("PIN sent successfully")
  }

  handleAPIError = (error: Error | APIError) => {
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  render() {
    const { showValidatePIN, didNotReceivePIN, pinDidNotMatch, requestNewPIN, showTooManyAttempts, loading } = this.state
    return (
      <>
        { showValidatePIN && <Modal.Dialog size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Enter your PIN</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row><Col><p>We are sending you a PIN. Please enter that PIN below:</p></Col></Row>
            <Row>
              <Col className="d-flex justify-content-center">
                <Form inline>
                  <Form.Control type="text" placeholder="XXXXXX" size="sm" disabled={loading} className="partial-pin-input" maxLength={6} minLength={6} onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => this.validatePIN(e)} />
                </Form>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="button-block">
            <Button variant="link" onClick={() => this.setState({ showValidatePIN: false, didNotReceivePIN: true })} className="mr-auto">I didn't receive my PIN!</Button>
            {!!this.props.onBack && <Button onClick={this.props.onBack}>Back</Button>}
            <CountdownButton variant="primary" className="mr-2" onClick={() => this.setState({ showValidatePIN: false, requestNewPIN: true })} waitSeconds={30} interval={1}>Resend</CountdownButton>
            <Button onClick={() => this.goToLogin()} variant="secondary">Cancel</Button>
          </Modal.Footer>
        </Modal.Dialog>}
        { didNotReceivePIN && <Modal.Dialog size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">I Didn't Receive My PIN</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row><Col><p>Our Apologies!</p></Col></Row>
            <Row><Col><p>For security purposes, we need to deliver your PIN to the phone number created when you were invited.</p></Col></Row>
            <Row><Col><p>If you believe the phone number set for your invitation is incorrect, or you are unable to receive a PIN at that number, please contact your administrator for assistance.</p></Col></Row>
          </Modal.Body>
          <Modal.Footer className="button-block">
            <Button onClick={() => this.goToLogin()}>Close</Button>
          </Modal.Footer>
        </Modal.Dialog>}
        { pinDidNotMatch && <Modal.Dialog size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">PIN Did Not Match</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row><Col><p>The PIN you entered did not match. Please request a new PIN</p></Col></Row>
            <Row>
              <Col>
                <ReCAPTCHA sitekey={ReCaptchaSiteKey} onErrored={() => this.setState({ validCaptcha: false })} onExpired={() => this.setState({ validCaptcha: false })} onChange={(value: string | null) => this.setState({ validCaptcha: value !== null })} hl="en" />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="button-block">
            <ActionButton disabled={!this.state.validCaptcha} onClick={() => this.requestNewPIN()} spin={loading} disableOnSpin>Request New PIN</ActionButton>
          </Modal.Footer>
        </Modal.Dialog>}
        { requestNewPIN && <Modal.Dialog size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Request New PIN</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row><Col><p>We're sorry you did not receive the PIN. Please request a new PIN below.</p></Col></Row>
            <Row>
              <Col>
                <ReCAPTCHA sitekey={ReCaptchaSiteKey} onErrored={() => this.setState({ validCaptcha: false })} onExpired={() => this.setState({ validCaptcha: false })} onChange={(value: string | null) => this.setState({ validCaptcha: value !== null })} hl="en" />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="button-block">
            <ActionButton disabled={!this.state.validCaptcha} onClick={() => this.requestNewPIN()} spin={loading} disableOnSpin>Request New PIN</ActionButton>
          </Modal.Footer>
        </Modal.Dialog>}
        { showTooManyAttempts && <Modal.Dialog size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Exceeded PIN attempts</Modal.Title>
          </Modal.Header>
          <Modal.Body>
          <Row><Col><p>Our Apologies!</p></Col></Row>
            <Row><Col><p>For security purposes, we limit the number of times an invitation may be accepted.</p></Col></Row>
            <Row><Col><p>In order to continue, your administrator will need to re-invite you. We have flagged your invitation to your administrator's attention.</p></Col></Row>
            <Row><Col><p>To expedite this process, please contact your administrator for assistance.</p></Col></Row>
          </Modal.Body>
          <Modal.Footer className="button-block">
            <Button onClick={() => this.goToLogin()}>Close</Button>
          </Modal.Footer>
        </Modal.Dialog>}
      </>
    )
  }
}
